import dom from '../utils/dom';
import FormSubmitHandler from './FormSubmitHandler';
import ControlGroup from './ControlGroup';
import { FormControlElement } from './controls/FormControl';
import createFormHandlerInput from './createFormHandlerInput';
import DefaultFormPostHandler from './DefaultFormPostHandler';
import IFormPostHandler from './IFormPostHandler';
import FormErrorMessenger from './FormErrorMessenger';
import DataAttributes from './DataAttributes';
import DefaultFormDataRetriever, { IFormDataRetriever } from './DefaultFormDataRetriever';
import type { ValidationResult } from './ValidationService';

export interface FormHandlerOnSubmitOptions {
  stopSubmitting: () => void;
  data?: any;
}

export type FormHandlerSubmitCallback = (options: FormHandlerOnSubmitOptions) => void;

type PostHandlerLike = IFormPostHandler | (new (formHandler: FormHandler) => IFormPostHandler);

export interface FormHandlerOptions {
  onValidationSuccess?: FormHandlerSubmitCallback;
  onValidationAjaxException?: () => void;
  postHandler?: PostHandlerLike,
  formDataRetriever?: IFormDataRetriever,
}

class FormHandler {
  $form: HTMLFormElement;

  submitHandler: FormSubmitHandler;

  controlGroups: Map<string, ControlGroup>;

  errorMessenger: FormErrorMessenger;

  postHandler: IFormPostHandler;

  formDataRetriever: IFormDataRetriever;

  constructor($form: HTMLFormElement, options: FormHandlerOptions = {}) {
    this.$form = $form;

    this.errorMessenger = this.initErrorMessenger();

    this.postHandler = getPostHandler(this, options.postHandler);

    this.controlGroups = this.findControlGroups();

    this.formDataRetriever = options.formDataRetriever || new DefaultFormDataRetriever(this);

    this.submitHandler = new FormSubmitHandler(this, options, this.errorMessenger);
    this.submitHandler.startListening();
  }

  get $submitBtn() {
    const $btn = dom.findFrom(this.$form, 'button[type=submit]');

    if ($btn) {
      return $btn as HTMLButtonElement;
    }

    return dom.find(`[form=${this.$form.id}]`) as HTMLButtonElement;
  }

  private findControlGroups() {
    const controlGroups = dom.findAllFrom(this.$form, `[${DataAttributes.ControlGroupFor}]`)
      .map(($el, index) => {
        const $inputs = dom.findAllFrom($el, '[data-tn-form-control]') as FormControlElement[];

        if (!$inputs.length) {
          return undefined;
        }

        const id = $el.getAttribute(DataAttributes.ControlGroupFor) as string;

        const input = createFormHandlerInput($inputs);

        const controlGroup = new ControlGroup(id, $el as HTMLElement, input, index);

        return controlGroup;
      })
      .filter((el) => el !== undefined) as Array<ControlGroup>;

    return new Map(controlGroups.map((el) => [el.id.toString(), el]));
  }

  initErrorMessenger() {
    const className = 'tn-form-error-message-container';
    const headingLevel = parseInt(this.$form.getAttribute('data-tn-form-error-messenger-heading-level') || '2', 10);

    let $el = dom.findFrom(this.$form, `.${className}`);

    if (!$el) {
      $el = dom.makeElement('div', { class: 'tn-form-error-message-container' });

      this.$form.prepend($el);
    }

    return new FormErrorMessenger(this, $el, headingLevel);
  }

  getFormData() {
    return this.formDataRetriever.getFormData();
  }

  getControlGroup(id: string) {
    return this.controlGroups.get(id);
  }

  getValue(id: string) {
    const controlGroup = this.controlGroups.get(id);

    if (controlGroup) {
      return controlGroup.input.value;
    }

    return undefined;
  }

  post() {
    return this.postHandler.post();
  }

  addErrorMessage(message: string, focus: boolean) {
    this.errorMessenger.messenger.addErrorMessage(message, focus);
  }

  clearErrorMessage() {
    this.errorMessenger.clear();
  }

  stopListening() {
    [...this.controlGroups.values()]
      .forEach((group) => group.stopListening());
  }

  startListening() {
    [...this.controlGroups.values()]
      .forEach((group) => group.startListening());
  }

  reloadControlGroups() {
    this.controlGroups = this.findControlGroups();
  }

  processValidationResults(validationResults: ValidationResult[]) {
    this.errorMessenger.processValidationResults(validationResults);
  }

  async submit(onSubmit?: FormHandlerSubmitCallback) {
    await this.submitHandler.submit(onSubmit);
  }
}

function getPostHandler(formHandler: FormHandler, PostHandler: PostHandlerLike | undefined) {
  if (!PostHandler) {
    return new DefaultFormPostHandler(formHandler);
  }

  if (typeof PostHandler === 'function') {
    return new PostHandler(formHandler);
  }

  return PostHandler;
}

export default FormHandler;
